import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { HiddenUnresolvedDomValues } from './HiddenUnresolvedDomValues'

export type OptionWithRenderedValue<T> = {
  value: T
  key: string
  renderedInputValue: ReactNode
  textValue: ReactNode
}

export const useResolvedRenderedValues = <T,>({
  options,
  enabled = true,
}: {
  options: OptionWithRenderedValue<T>[]
  enabled?: boolean
}) => {
  // Hidden mirrors let us read eventual textContent for components that render
  // their label asynchronously (for example after data fetching).
  const textExtractionRefs = useRef<Record<string, HTMLSpanElement | null>>({})
  const [resolvedRenderedValues, setResolvedRenderedValues] = useState<
    Record<string, string>
  >({})

  const optionsRequiringResolution = useMemo(
    () =>
      enabled
        ? options.filter(
            ({ textValue, renderedInputValue }) =>
              typeof textValue === 'string' &&
              textValue === '' &&
              typeof renderedInputValue !== 'string',
          )
        : [],
    [enabled, options],
  )

  useEffect(() => {
    const syncResolvedValues = () => {
      setResolvedRenderedValues((previousValues) => {
        let changed = false
        const nextValues = { ...previousValues }
        const optionKeys = new Set(options.map(({ key }) => key))

        for (const { key } of optionsRequiringResolution) {
          const resolvedText =
            textExtractionRefs.current[key]?.textContent?.trim()

          if (resolvedText && previousValues[key] !== resolvedText) {
            nextValues[key] = resolvedText
            changed = true
          }
        }

        // Keep state aligned with current options when the list changes.
        for (const key of Object.keys(nextValues)) {
          if (!optionKeys.has(key)) {
            delete nextValues[key]
            changed = true
          }
        }

        return changed ? nextValues : previousValues
      })
    }

    if (!enabled) {
      setResolvedRenderedValues((previousValues) =>
        Object.keys(previousValues).length === 0 ? previousValues : {},
      )
      return
    }

    syncResolvedValues()

    if (optionsRequiringResolution.length === 0) {
      return
    }

    // Observe text mutations because async renderers may update after mount.
    const observer = new MutationObserver(() => {
      syncResolvedValues()
    })

    for (const { key } of optionsRequiringResolution) {
      const element = textExtractionRefs.current[key]

      if (element) {
        observer.observe(element, {
          childList: true,
          subtree: true,
          characterData: true,
        })
      }
    }

    return () => {
      observer.disconnect()
    }
  }, [enabled, options, optionsRequiringResolution])

  const extractionMirrors = useMemo(
    () =>
      enabled ? (
        <HiddenUnresolvedDomValues
          optionsRequiringTextExtraction={optionsRequiringResolution}
          textExtractionRefs={textExtractionRefs}
        />
      ) : null,
    [enabled, optionsRequiringResolution],
  )

  return {
    resolvedRenderedValues,
    extractionMirrors,
  }
}
